<template>
    <div class="gpa-addr-wrap">
        <input v-if="!selectedAddress && !showManualEntry" ref="autocomplete" v-model="address" type="text" placeholder="Enter your address" :disabled="!isGoogleLoaded" class="gpa-addr-input" @blur="handleBlur" :style="inputStyle"/>
        <div v-if="selectedAddress || showManualEntry">
            <div class="gpa-addr-row">
                <div class="gpa-addr-street"><input class="gpa-addr-input" v-model="manualEntry.street" maxlength="256" placeholder="Street Address" type="text" @blur="onAddressChange" :style="inputStyle" required></div>
                <div class="gpa-addr-apt"><input class="gpa-addr-input" v-model="manualEntry.address_line2" maxlength="256" placeholder="Apt #" type="text" @blur="onAddressChange" :style="inputStyle"></div>
            </div>

            <div class="gpa-addr-row gpa-addr-csz">
                <input v-model="manualEntry.city" class="gpa-addr-input gpa-addr-city" maxlength="256" placeholder="City" type="text" required @input="onAddressChange" :style="inputStyle">
                <div class="gpa-addr-state-wrap">
                    <input v-model="manualEntry.state" class="gpa-addr-input gpa-addr-state" maxlength="2" placeholder="State" type="text" required @input="handleStateInput" @focus="stateDropOpen = true" @blur="closeStateDrop" :style="inputStyle" autocomplete="address-level1">
                    <div v-if="stateDropOpen && filteredStates.length > 0" class="gpa-state-drop">
                        <div v-for="s in filteredStates" :key="s" class="gpa-state-opt" @mousedown.prevent="selectState(s)">{{ s }}</div>
                    </div>
                </div>
                <input v-model="manualEntry.zip" class="gpa-addr-input gpa-addr-zip" maxlength="10" placeholder="ZIP" type="text" required @input="onAddressChange" :style="inputStyle">
            </div>
            <!-- Shows the moment LA / MS is typed, pasted or picked; the checkout's payment gate refuses it too. -->
            <div v-if="stateBlocked" class="w-form-fail gpa-state-blocked" role="alert">{{ blockedStateMessage }}</div>
        </div>
    </div>
</template>

<script>
import { BLOCKED_STATE_MESSAGE, isBlockedState } from '@/utils/blockedStates';

export default {
    name: 'GooglePlacesAutocomplete',
    props: {
        apiKey: {
            type: String,
            required: true
        },
        borderColor: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            address: '',
            autocomplete: null,
            selectedAddress: null,
            manualEntry: {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            },
            isGoogleLoaded: false,
            showManualEntry: false,
            stateDropOpen: false,
            blockedStateMessage: BLOCKED_STATE_MESSAGE,
            // No LA or MS: GLP-1 cannot be sold in Louisiana or Mississippi.
            usStates: [
                'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
                'HI','ID','IL','IN','IA','KS','KY','ME','MD',
                'MA','MI','MN','MO','MT','NE','NV','NH','NJ',
                'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
                'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
                'DC','PR','VI','GU','AS','MP'
            ]
        }
    },
    computed: {
        stateBlocked() {
            return isBlockedState(this.manualEntry.state);
        },
        filteredStates() {
            const q = (this.manualEntry.state || '').toUpperCase();
            if (!q) return this.usStates;
            return this.usStates.filter(s => s.startsWith(q));
        },
        inputStyle() {
            if (!this.borderColor) {
                return {}
            }
            return {
                borderColor: this.borderColor,
                borderWidth: '1px',
                borderStyle: 'solid'
            }
        }
    },
    watch: {
        selectedAddress: {
            handler(newVal) {
                if (newVal) {
                    this.manualEntry = {
                        street: newVal.street,
                        city: newVal.city,
                        state: newVal.state,
                        zip: newVal.zip,
                        address_line2: newVal.address_line2 || ''
                    };
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.loadGoogleMapsAPI()
    },
    methods: {
        loadGoogleMapsAPI() {
            const script = document.createElement('script')
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
            script.async = true
            script.defer = true
            script.onload = () => {
                this.isGoogleLoaded = true
                this.initAutocomplete()
            }
            document.head.appendChild(script)
        },
        initAutocomplete() {
            if (!window.google) {
                console.error('Google Maps API not loaded')
                return
            }
            const options = {
                types: ['address'],
                componentRestrictions: {
                    country: 'us'
                }
            }
            this.autocomplete = new window.google.maps.places.Autocomplete(
                this.$refs.autocomplete,
                options
            )
            this.autocomplete.addListener('place_changed', this.handlePlaceSelect)
        },
        handlePlaceSelect() {
            const place = this.autocomplete.getPlace()
            if (!place.geometry) {
                console.log("No details available for input: '" + place.name + "'")
                return
            }

            this.selectedAddress = {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            }

            for (const component of place.address_components) {
                const type = component.types[0]
                if (type === 'street_number') {
                    this.selectedAddress.street = component.long_name + ' '
                }
                if (type === 'route') {
                    this.selectedAddress.street += component.long_name
                }
                if (type === 'locality') {
                    this.selectedAddress.city = component.long_name
                }
                if (type === 'administrative_area_level_1') {
                    this.selectedAddress.state = component.short_name
                }
                if (type === 'postal_code') {
                    this.selectedAddress.zip = component.long_name
                }
            }

            // Attempt to extract address line 2
            const formattedAddress = place.formatted_address
            const parts = formattedAddress.split(',')
            if (parts.length > 1) {
                const firstPart = parts[0].trim()
                if (firstPart.includes(' ')) {
                    const potentialSubpremise = firstPart.split(' ').pop()
                    if (potentialSubpremise.match(/^\d+[a-zA-Z]?$|^[a-zA-Z]$/)) {
                        this.selectedAddress.address_line2 = potentialSubpremise
                        this.selectedAddress.street = this.selectedAddress.street.replace(potentialSubpremise, '').trim()
                    }
                }
            }

            this.$emit('address-updated', this.selectedAddress)
        },
        handleBlur() {
            setTimeout(() => {
                if (this.address && this.address.trim() !== '' && !this.selectedAddress) {
                    this.showManualEntry = true;

                    const parts = this.address.split(',');
                    if (parts.length > 0) {
                        this.manualEntry.street = parts[0].trim();
                    }

                    if (parts.length > 1) {
                        const cityPart = parts[1].trim();
                        this.manualEntry.city = cityPart;
                    }

                    if (parts.length > 2) {
                        const statePart = parts[2].trim();
                        const stateZipMatch = statePart.match(/([A-Z]{2})\s+(\d{5}(-\d{4})?)/);
                        if (stateZipMatch) {
                            this.manualEntry.state = stateZipMatch[1];
                            this.manualEntry.zip = stateZipMatch[2];
                        } else {
                            // Try to match just a 2-letter state code
                            const stateOnly = statePart.match(/^([A-Z]{2})$/);
                            this.manualEntry.state = stateOnly ? stateOnly[1] : '';
                        }
                    }
                    // A typed LA / MS address must reach the checkout's payment gate now,
                    // not on the next field edit.
                    if (this.stateBlocked) this.onAddressChange();
                }
            }, 200);
        },
        handleStateInput() {
            this.manualEntry.state = this.manualEntry.state.toUpperCase().replace(/[^A-Z]/g, '');
            this.stateDropOpen = true;
            // A blocked state goes up too, or the checkout would still hold the last
            // good state and its payment gate would never see LA / MS.
            if (this.usStates.includes(this.manualEntry.state) || this.stateBlocked) {
                this.onAddressChange();
            }
        },
        selectState(s) {
            this.manualEntry.state = s;
            this.stateDropOpen = false;
            this.onAddressChange();
        },
        closeStateDrop() {
            setTimeout(() => { this.stateDropOpen = false; }, 150);
        },
        onAddressChange() {
            this.$emit('address-updated', this.manualEntry);
        },
        resetAddress() {
            this.selectedAddress = null;
            this.address = '';
            this.showManualEntry = false;
            this.manualEntry = {
                street: '',
                city: '',
                state: '',
                zip: '',
                address_line2: ''
            };
            this.$nextTick(() => {
                this.$refs.autocomplete.focus();
            });
        }
    }
}
</script>

<style scoped>
.gpa-addr-wrap {
    width: 100%;
}

.gpa-addr-input {
    width: 100%;
    height: 48px;
    padding: 10px 14px;
    font-size: 15px;
    font-family: inherit;
    color: #1a1a1a;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
}

@media screen and (max-width: 991px) {
    .gpa-addr-input {
        height: 52px !important;
        box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02) !important;
    }
}

.gpa-addr-input:focus {
    border-color: var(--logo-blue, #52b2cf);
    box-shadow: 0 0 0 3px rgba(82, 178, 207, 0.15);
}

.gpa-addr-input::placeholder {
    color: #9ca3af;
    font-weight: 400;
}

/* webflow.css hides .w-form-fail until a form fails; this one shows whenever it renders. */
.gpa-state-blocked {
    display: block;
}

/* State typeahead dropdown */
.gpa-addr-state-wrap {
    position: relative;
    flex: 1;
    min-width: 80px;
    max-width: 100px;
}

.gpa-addr-state-wrap .gpa-addr-state {
    text-transform: uppercase;
}

.gpa-state-drop {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d1d5db;
    border-top: none;
    border-radius: 0 0 8px 8px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.gpa-state-opt {
    padding: 8px 14px;
    font-size: 14px;
    cursor: pointer;
    color: #1a1a1a;
}

.gpa-state-opt:hover {
    background: #f3f4f6;
}

.gpa-addr-row {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
}

.gpa-addr-street {
    flex: 2;
}

.gpa-addr-apt {
    flex: 1;
    max-width: 120px;
}

.gpa-addr-csz {
    display: flex;
    gap: 10px;
}

.gpa-addr-city {
    flex: 2;
}


.gpa-addr-zip {
    flex: 1;
    min-width: 80px;
    max-width: 110px;
}

@media screen and (max-width: 479px) {
    .gpa-addr-input {
        height: 44px;
        font-size: 14px;
        padding: 8px 12px;
    }

    .gpa-addr-row {
        gap: 8px;
        margin-bottom: 8px;
    }

    .gpa-addr-apt {
        max-width: 90px;
    }

    .gpa-addr-state-wrap {
        max-width: 85px;
    }

    .gpa-addr-zip {
        max-width: 90px;
    }
}
</style>
